import React, { useEffect, useState, useRef } from 'react';
import { Snackbar } from '@mui/material';
import { Onfido } from 'onfido-sdk-ui';

import Alert from '../alert/alert.component';
import Loader from '../loader/loader.component';

import './verification.scss';

interface VerifyIdentityDocumentProps {
  inquiryId: string;
  sessionToken: string;
  onComplete?: (data: any) => void;
  onCancel?: () => void;
  onError?: (error: string) => void;
}

export const VerifyIdentityDocument: React.FC<VerifyIdentityDocumentProps> = ({
  inquiryId,
  sessionToken,
  onComplete,
  onCancel,
  onError,
}) => {
  const [alertOpen, setAlertOpen] = useState(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const onfidoInitialized = useRef(false);

  const initializeOnfido = (token: string, workflowRunId: string) => {
    try {
      Onfido.init({
        token,
        containerId: 'entrust-widget',
        workflowRunId,
        smsNumberCountryCode: 'US',
        onComplete: (data) => {
          if (onComplete) {
            onComplete(data);
          }
        },
        onError: (error) => {
          const errorMessage = error.message || 'Verification failed';
          setError(errorMessage);
          setAlertOpen(true);
          if (onError) {
            onError(errorMessage);
          }
        },
      });
      setIsLoading(false);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to initialize verification';
      setError(errorMessage);
      setAlertOpen(true);
      setIsLoading(false);
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  useEffect(() => {
    if (!onfidoInitialized.current && sessionToken && inquiryId) {
      initializeOnfido(sessionToken, inquiryId);
      onfidoInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken, inquiryId]);

  const closeAlert = () => {
    setAlertOpen(false);
  };

  const handleRetry = () => {
    setError('');
    setIsLoading(true);
    onfidoInitialized.current = false;
    initializeOnfido(sessionToken, inquiryId);
  };

  if (isLoading) {
    return (
      <section className="entrust-verification">
        <div className="container">
          <div className="entrust-container">
            <div className="entrust-loading">
              <Loader />
              <p className="loading-text">Initializing identity verification...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="entrust-verification">
        <div className="container">
          <div className="entrust-container">
            <div className="entrust-error">
              <span className="error-icon fa fa-exclamation-triangle"></span>
              <p className="error-message">{error}</p>
              <button className="btn-register" onClick={handleRetry}>
                Try Again
              </button>
              {onCancel && (
                <button
                  className="btn-secondary"
                  style={{ marginTop: '10px' }}
                  onClick={onCancel}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        <Snackbar
          open={alertOpen}
          autoHideDuration={6000}
          onClose={closeAlert}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={closeAlert} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </section>
    );
  }

  return (
    <section className="entrust-verification">
      <div className="container">
        <div className="entrust-container">
          <div id="entrust-widget"></div>
        </div>
      </div>
    </section>
  );
};

export default VerifyIdentityDocument;
